// (NO CHANGES TO EXISTING IMPORTS)
import React, { Suspense, lazy, useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
  useLocation,
} from "react-router-dom";
import MainLayout from "./layouts/MainLayout";
import LandingPage from "./pages/LandingPage";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import CompanyInvite from "./pages/CompanyInvite";
import Home from "./pages/Home";
import ReferencePage from "./pages/ReferencePage";
import Calculator from "./pages/Calculator";
import BillingPage from "./pages/BillingPage";
import { auth, db } from "./services/firebase";
import { getIdTokenResult, onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getTrialStatus } from "./utils/trialStatus";
import { getAccessState } from "./utils/accessControl";
import { ensurePlatformOwnerClaim } from "./utils/platformOwnerService";
import { claimFounderAccess } from "./utils/founderProgramService";
import {
  startWorkspaceCloudSync,
  stopWorkspaceCloudSync,
} from "./utils/workspaceCloudSync";
import { getAllJobs, mergeCloudJobs } from "./utils/jobStorage";
import {
  getRecords,
  getShopRecordTypes,
  mergeCloudRecords,
} from "./utils/shopDataStore";
import { redirectToCheckout } from "./services/stripe";
import { APP_ROUTE_PATHS } from "./appRoutePaths";
import "./App.css";
import "./responsive.css";

const MONTHLY_PRICE_ID = process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID || "";

const lazyWithChunkRecovery = (importer) =>
  lazy(() =>
    importer().catch((error) => {
      const message = String(error?.message || error || "");
      const isChunkError =
        error?.name === "ChunkLoadError" ||
        message.includes("Loading chunk") ||
        message.includes("Failed to fetch dynamically imported module");

      if (
        isChunkError &&
        typeof window !== "undefined" &&
        window.sessionStorage.getItem("manucalc_chunk_recovered") !== "true"
      ) {
        window.sessionStorage.setItem("manucalc_chunk_recovered", "true");
        window.location.reload();
      }

      throw error;
    })
  );

const AdminDashboard = lazyWithChunkRecovery(() => import("./pages/Admin/AdminDashboard.js"));
const JobsDashboard = lazyWithChunkRecovery(() => import("./pages/jobs/JobsDashboard"));
const JobDetail = lazyWithChunkRecovery(() => import("./pages/jobs/JobDetail"));
const ProcessBuilder = lazyWithChunkRecovery(() => import("./pages/ProcessBuilder"));
const ToolDatabase = lazyWithChunkRecovery(() => import("./pages/ToolDatabase"));
const FixtureLibrary = lazyWithChunkRecovery(() => import("./pages/FixtureLibrary"));
const MachineDatabase = lazyWithChunkRecovery(() => import("./pages/MachineDatabase"));
const SetupSheets = lazyWithChunkRecovery(() => import("./pages/SetupSheets"));
const JobTravelers = lazyWithChunkRecovery(() => import("./pages/JobTravelers"));
const InspectionSheets = lazyWithChunkRecovery(() => import("./pages/InspectionSheets"));
const ShopBrain = lazyWithChunkRecovery(() => import("./pages/ShopBrain"));
const CapacityPlanner = lazyWithChunkRecovery(() => import("./pages/CapacityPlanner"));
const ShopFloorMode = lazyWithChunkRecovery(() => import("./pages/ShopFloorMode"));
const AIAssistant = lazyWithChunkRecovery(() => import("./pages/AIAssistant"));
const QRCodeCenter = lazyWithChunkRecovery(() => import("./pages/QRCodeCenter"));
const ExportCenter = lazyWithChunkRecovery(() => import("./pages/ExportCenter"));
const ThreeSPage = lazyWithChunkRecovery(() => import("./pages/ThreeSPage"));
const FeatureRequestPage = lazyWithChunkRecovery(() => import("./pages/FeatureRequestPage"));
const FeatureRequestsAdmin = lazyWithChunkRecovery(() => import("./pages/Admin/FeatureRequestsAdmin"));
const MessagesPage = lazyWithChunkRecovery(() => import("./pages/MessagesPage"));
const CollaborativeProjects = lazyWithChunkRecovery(() => import("./pages/CollaborativeProjects"));

const DEFAULT_PREFERENCES = {
  useImperial: true,
  learnMode: false,
  darkTheme: true,
};

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(previousProps) {
    if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error, info) {
    console.error("ManuCalc route error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="route-error-panel">
          <span>Route Error</span>
          <h2>This screen failed to load.</h2>
          <p>
            Refresh the page once. If it happens again, open the dashboard and
            try the calculator again.
          </p>
          <div>
            <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
              Refresh
            </button>
            <a className="btn btn-secondary" href="/dashboard">
              Back to Calculators
            </a>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkoutRedirecting, setCheckoutRedirecting] = useState(false);
  const [billingStateLoaded, setBillingStateLoaded] = useState(false);
  const [accountProfile, setAccountProfile] = useState(null);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);

  const hasTriggeredCheckoutRef = useRef(false);

  useEffect(() => {
    const authFallbackTimer = window.setTimeout(() => {
      setAuthReady((ready) => ready || true);
    }, 3500);

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      window.clearTimeout(authFallbackTimer);
      setUser(currentUser || null);
      if (currentUser) {
        getIdTokenResult(currentUser)
          .then(async (token) => {
            if (token.claims.platformOwner === true) {
              setIsPlatformOwner(true);
              return;
            }
            const activated = await ensurePlatformOwnerClaim(currentUser, token);
            setIsPlatformOwner(activated);
          })
          .catch(() => setIsPlatformOwner(false));
      } else {
        setIsPlatformOwner(false);
      }
      setAuthReady(true);
      hasTriggeredCheckoutRef.current = false;
    });

    return () => {
      window.clearTimeout(authFallbackTimer);
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user?.uid || !accountProfile) {
      stopWorkspaceCloudSync();
      return undefined;
    }

    let disposed = false;
    let cleanup = () => {};
    startWorkspaceCloudSync({
      user,
      accountProfile,
      getLocalJobs: getAllJobs,
      mergeCloudJobs,
      getRecordTypes: getShopRecordTypes,
      getLocalRecords: getRecords,
      mergeCloudRecords,
    })
      .then((stop) => {
        if (disposed) stop();
        else cleanup = stop;
      })
      .catch((error) => {
        console.warn("Workspace cloud sync could not start:", error);
      });

    return () => {
      disposed = true;
      cleanup();
    };
  }, [accountProfile, user]);

  useEffect(() => {
    let mounted = true;

    async function loadUserData() {
      if (!authReady) return;

      if (!user?.uid) {
        if (mounted) {
          setPreferences(DEFAULT_PREFERENCES);
          setTrialStatus(null);
          setSubscriptionStatus(null);
          setPreferencesReady(true);
          setBillingStateLoaded(true);
          setCheckoutRedirecting(false);
          setAccountProfile(null);
        }
        return;
      }

      try {
        setPreferencesReady(false);
        setBillingStateLoaded(false);
      setAccountProfile(null);

        const userRef = doc(db, "users", user.uid);
        let userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          const now = Date.now();
          await setDoc(
            userRef,
            {
              email: user.email || "",
              username: user.displayName || String(user.email || "").split("@")[0],
              displayName: user.displayName || String(user.email || "").split("@")[0],
              accountType: "personal",
              accountBillingType: "personal",
              billingExempt: false,
              subscriptionRequired: true,
              role: "personal",
              subscriptionStatus: "trial",
              trialStartDate: new Date(now).toISOString(),
              trialEndDate: new Date(now + 60 * 60 * 1000).toISOString(),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          userSnap = await getDoc(userRef);
        }

        let userData = userSnap.exists() ? userSnap.data() : null;

        if (userData && userData.isFounder !== true) {
          try {
            const founderResult = await claimFounderAccess(user);
            if (founderResult?.isFounder === true) {
              userSnap = await getDoc(userRef);
              userData = userSnap.exists() ? userSnap.data() : userData;
            }
          } catch (founderError) {
            console.warn("Founder access could not be checked:", founderError);
          }
        }

        if (mounted) {
          setPreferences({
            useImperial:
              userData?.preferences?.useImperial ??
              DEFAULT_PREFERENCES.useImperial,
            learnMode:
              userData?.preferences?.learnMode ??
              DEFAULT_PREFERENCES.learnMode,
            darkTheme: true,
          });

          const trial = getTrialStatus(
            userData?.trialStartDate,
            userData?.trialEndDate
          );

          setTrialStatus(trial);
          setSubscriptionStatus(userData?.subscriptionStatus || "none");
          setAccountProfile({
            accountType: userData?.accountType || "personal",
            accountBillingType: userData?.accountBillingType || "personal",
            billingExempt: Boolean(userData?.billingExempt),
            subscriptionRequired: userData?.subscriptionRequired !== false,
            subscriptionStatus: userData?.subscriptionStatus || "none",
            role: userData?.role || "personal",
            companyId: userData?.companyId || "",
            companyName: userData?.companyName || "",
            companyOwnerUid: userData?.companyOwnerUid || "",
            seatCount: userData?.seatCount || 1,
            adminDashboardEnabled: Boolean(userData?.adminDashboardEnabled),
            canExport: Boolean(userData?.canExport),
            isFounder: Boolean(userData?.isFounder),
            founderNumber: userData?.founderNumber || null,
            founderGrantedAt: userData?.founderGrantedAt || null,
          });
          setBillingStateLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load user data:", error);

        if (mounted) {
          setPreferences(DEFAULT_PREFERENCES);
          setTrialStatus(null);
          setSubscriptionStatus(null);
          setAccountProfile(null);
          setBillingStateLoaded(true);
        }
      } finally {
        if (mounted) {
          setPreferencesReady(true);
        }
      }
    }

    loadUserData();

    return () => {
      mounted = false;
    };
  }, [authReady, user]);

  useEffect(() => {
    document.body.setAttribute("data-theme", "dark");

    return () => {
      document.body.removeAttribute("data-theme");
    };
  }, []);

  const handleAuth = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setPreferences(DEFAULT_PREFERENCES);
      setTrialStatus(null);
      setSubscriptionStatus(null);
      setCheckoutRedirecting(false);
      setBillingStateLoaded(false);
      hasTriggeredCheckoutRef.current = false;
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const accessState = getAccessState({
    user,
    trialStatus,
    subscriptionStatus,
    accountProfile,
  });

  const isSubscribed = accessState.isSubscribed;
  const isTrialExpired = !!trialStatus?.isTrialExpired;

  const isCompanySeatAccount =
    accountProfile?.isFounder === true ||
    accountProfile?.accountBillingType === "companySeat" ||
    accountProfile?.billingExempt === true ||
    accountProfile?.subscriptionRequired === false ||
    (
      accountProfile?.accountType === "company" &&
      accountProfile?.role !== "owner" &&
      accountProfile?.companyOwnerUid
    );

  const hasAccess = accessState.hasAccess || isCompanySeatAccount;

  const userRole = accountProfile?.role || "personal";

  const isCompanyOwner =
    accountProfile?.accountType === "company" &&
    userRole === "owner" &&
    accountProfile?.adminDashboardEnabled;

  const rolePermissions = {
    owner: {
      canUseAdmin: true,
      canUseProjects: true,
      canUseJobs: true,
      canUseCalculators: true,
      canUseSavedWork: true,
      canUseSettings: true,
      canExport: true,
      canManageShopData: true,
    },
    supervisor: {
      canUseAdmin: false,
      canUseProjects: true,
      canUseJobs: true,
      canUseCalculators: true,
      canUseSavedWork: true,
      canUseSettings: true,
      canExport: false,
      canManageShopData: true,
    },
    machinist: {
      canUseAdmin: false,
      canUseProjects: false,
      canUseJobs: true,
      canUseCalculators: true,
      canUseSavedWork: true,
      canUseSettings: true,
      canExport: false,
      canManageShopData: true,
    },
    estimator: {
      canUseAdmin: false,
      canUseProjects: true,
      canUseJobs: true,
      canUseCalculators: true,
      canUseSavedWork: true,
      canUseSettings: true,
      canExport: false,
      canManageShopData: true,
    },
    viewer: {
      canUseAdmin: false,
      canUseProjects: false,
      canUseJobs: true,
      canUseCalculators: true,
      canUseSavedWork: true,
      canUseSettings: true,
      canExport: false,
      canManageShopData: false,
    },
    personal: {
      canUseAdmin: false,
      canUseProjects: true,
      canUseJobs: true,
      canUseCalculators: true,
      canUseSavedWork: true,
      canUseSettings: true,
      canExport: true,
      canManageShopData: true,
    },
  };

  const basePermissions = rolePermissions[userRole] || rolePermissions.personal;
  const activePermissions = {
    ...basePermissions,
    canExport:
      basePermissions.canExport ||
      isCompanyOwner ||
      accountProfile?.canExport === true,
  };

  useEffect(() => {
    if (
      !user?.uid ||
      !authReady ||
      !preferencesReady ||
      !billingStateLoaded ||
      checkoutRedirecting ||
      hasTriggeredCheckoutRef.current
    ) {
      return;
    }

    if (isSubscribed || isCompanySeatAccount || accessState.isFounder) {
      hasTriggeredCheckoutRef.current = false;
      setCheckoutRedirecting(false);
      return;
    }

    if (isTrialExpired) {
      const startCheckoutRedirect = async () => {
        try {
          hasTriggeredCheckoutRef.current = true;
          setCheckoutRedirecting(true);

          await redirectToCheckout({
            email: user.email,
            uid: user.uid,
            priceId: MONTHLY_PRICE_ID,
            accountType: accountProfile?.accountBillingType || "personal",
          });
        } catch (error) {
          console.error("Checkout redirect error:", error);
          hasTriggeredCheckoutRef.current = false;
          setCheckoutRedirecting(false);
        }
      };

      startCheckoutRedirect();
    }
  }, [
    user,
    authReady,
    preferencesReady,
    billingStateLoaded,
    isTrialExpired,
    isSubscribed,
    isCompanySeatAccount,
    accessState.isFounder,
    checkoutRedirecting,
    accountProfile?.accountBillingType,
  ]);

  const renderProtectedPage = (content, options = {}) => {
    const {
      allowWithoutAccess = false,
      requireCompanyOwner = false,
      requiredPermission = "",
    } = options;

    if (!user) return <Redirect to="/auth" />;

    if (checkoutRedirecting) {
      return (
        <MainLayout
          user={user}
          onLogout={handleLogout}
          preferences={preferences}
          accountProfile={accountProfile}
          isCompanyOwner={isCompanyOwner}
          rolePermissions={activePermissions}
          isPlatformOwner={isPlatformOwner}
        >
          <div className="loading-screen">
            <div className="loading-spinner">💳</div>
            <p>Redirecting to billing...</p>
          </div>
        </MainLayout>
      );
    }

    if (!hasAccess && !allowWithoutAccess) {
      return <Redirect to="/" />;
    }

    if (requireCompanyOwner && !isCompanyOwner) {
      return <Redirect to="/" />;
    }

    if (requiredPermission && !activePermissions?.[requiredPermission]) {
      return <Redirect to="/" />;
    }

    return (
      <MainLayout
        user={user}
        onLogout={handleLogout}
        preferences={preferences}
        accountProfile={accountProfile}
        isCompanyOwner={isCompanyOwner}
        rolePermissions={activePermissions}
        isPlatformOwner={isPlatformOwner}
      >
        <RouteErrorBoundary resetKey={`${window.location.pathname}${window.location.search}`}>
          <Suspense fallback={<div className="route-loading">Loading workspace...</div>}>
            {content}
          </Suspense>
        </RouteErrorBoundary>
      </MainLayout>
    );
  };

  const renderAppPage = (content) => (
    <MainLayout
      showFooter
      user={user}
      onLogout={handleLogout}
      preferences={preferences}
      accountProfile={accountProfile}
      isCompanyOwner={isCompanyOwner}
      rolePermissions={activePermissions}
      isPlatformOwner={isPlatformOwner}
    >
      {content}
    </MainLayout>
  );

  if (!authReady || !preferencesReady || !billingStateLoaded || checkoutRedirecting) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner">
          {checkoutRedirecting ? "💳" : "⚙️"}
        </div>
        <p>
          {checkoutRedirecting
            ? "Redirecting to billing..."
            : "Loading ManuCalc..."}
        </p>
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <div
        className="App theme-dark"
      >
        <Switch>
          <Route
            exact
            path="/"
            render={() =>
              user ? (
                hasAccess ? (
                  <MainLayout
                    user={user}
                    onLogout={handleLogout}
                    preferences={preferences}
                    accountProfile={accountProfile}
                    isCompanyOwner={isCompanyOwner}
                    rolePermissions={activePermissions}
                    isPlatformOwner={isPlatformOwner}
                  >
                    <Home user={user} preferences={preferences} />
                  </MainLayout>
                ) : (
                  <MainLayout
                    showFooter
                    user={user}
                    onLogout={handleLogout}
                    preferences={preferences}
                    accountProfile={accountProfile}
                    isCompanyOwner={isCompanyOwner}
                    rolePermissions={activePermissions}
                    isPlatformOwner={isPlatformOwner}
                  >
                    <LandingPage
                      user={user}
                      preferences={preferences}
                      trialStatus={trialStatus}
                      subscriptionStatus={subscriptionStatus}
                      hasAccess={hasAccess}
                    />
                  </MainLayout>
                )
              ) : (
                <MainLayout
                  showFooter
                  user={user}
                  onLogout={handleLogout}
                  preferences={preferences}
                  accountProfile={accountProfile}
                  isCompanyOwner={isCompanyOwner}
                  rolePermissions={activePermissions}
                  isPlatformOwner={isPlatformOwner}
                >
                  <LandingPage
                    user={user}
                    preferences={preferences}
                    trialStatus={trialStatus}
                    subscriptionStatus={subscriptionStatus}
                    hasAccess={hasAccess}
                  />
                </MainLayout>
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.auth}
            render={() =>
              user ? <Redirect to="/" /> : <Auth onAuth={handleAuth} />
            }
          />

          <Route
            exact
            path="/company-invite/:token"
            render={() => <CompanyInvite user={user} />}
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.dashboard}
            render={() =>
              renderProtectedPage(
                <Dashboard user={user} preferences={preferences} />
              )
            }
          />

          <Route
            exact
            path="/settings"
            render={() =>
              renderProtectedPage(
                <Settings
                  user={user}
                  preferences={preferences}
                  setPreferences={setPreferences}
                />,
                { allowWithoutAccess: true, requiredPermission: "canUseSettings" }
              )
            }
          />

          <Route exact path="/billing" render={() =>
            renderProtectedPage(<BillingPage user={user} accountProfile={accountProfile} />, {
              allowWithoutAccess: true,
              requiredPermission: "canUseSettings",
            })
          } />

          <Route exact path="/billing/success" render={() =>
            renderProtectedPage(<BillingPage user={user} accountProfile={accountProfile} />, {
              allowWithoutAccess: true,
              requiredPermission: "canUseSettings",
            })
          } />

          <Route exact path="/billing/cancel" render={() =>
            renderProtectedPage(<BillingPage user={user} accountProfile={accountProfile} />, {
              allowWithoutAccess: true,
              requiredPermission: "canUseSettings",
            })
          } />

          <Route exact path="/billing/return" render={() =>
            renderProtectedPage(<BillingPage user={user} accountProfile={accountProfile} />, {
              allowWithoutAccess: true,
              requiredPermission: "canUseSettings",
            })
          } />

          <Route
            exact
            path="/formula-reference"
            render={() => renderAppPage(<ReferencePage type="formulas" />)}
          />

          <Route
            exact
            path="/material-data"
            render={() => renderAppPage(<ReferencePage type="materials" />)}
          />

          <Route
            exact
            path="/resources"
            render={() => renderAppPage(<ReferencePage type="resources" />)}
          />

          <Route
            exact
            path="/whats-new"
            render={() => renderAppPage(<ReferencePage type="whatsNew" />)}
          />

          <Route
            exact
            path="/about"
            render={() => renderAppPage(<ReferencePage type="about" />)}
          />

          <Route exact path="/request-custom-tool" render={() =>
            renderProtectedPage(<FeatureRequestPage user={user} accountProfile={accountProfile} />, {
              requiredPermission: "canUseCalculators",
            })
          } />

          <Route exact path="/platform-admin/feature-requests" render={() =>
            renderProtectedPage(<FeatureRequestsAdmin user={user} />, {
              requiredPermission: "canUseCalculators",
            })
          } />

          <Route exact path="/owner/requests" render={() =>
            renderProtectedPage(<FeatureRequestsAdmin user={user} />, {
              requiredPermission: "canUseCalculators",
            })
          } />

          <Route exact path="/collaborative-projects" render={() =>
            renderProtectedPage(<CollaborativeProjects user={user} accountProfile={accountProfile} />, {
              requiredPermission: "canUseProjects",
            })
          } />

          <Route exact path="/messages" render={() =>
            renderProtectedPage(<MessagesPage user={user} accountProfile={accountProfile} />, {
              requiredPermission: "canUseJobs",
            })
          } />

          <Route
            exact
            path="/saved-jobs"
            render={() =>
              renderProtectedPage(<Redirect to="/jobs" />, {
                requiredPermission: "canUseSavedWork",
              })
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.admin}
            render={() =>
              renderProtectedPage(
                <AdminDashboard user={user} preferences={preferences} />,
                { requireCompanyOwner: true, requiredPermission: "canUseAdmin" }
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.jobs}
            render={() =>
              renderProtectedPage(
                <JobsDashboard user={user} preferences={preferences} />,
                { requiredPermission: "canUseJobs" }
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.jobDetail}
            render={({ match }) =>
              renderProtectedPage(
                <JobDetail
                  user={user}
                  preferences={preferences}
                  jobId={match.params.jobId}
                />,
                { requiredPermission: "canUseJobs" }
              )
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.processBuilder}
            render={() =>
              renderProtectedPage(<ProcessBuilder />, {
                requiredPermission: "canUseJobs",
              })
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.processBuilderDetail}
            render={({ match }) =>
              renderProtectedPage(<ProcessBuilder jobId={match.params.jobId} />, {
                requiredPermission: "canUseJobs",
              })
            }
          />

          <Route
            exact
            path="/tools"
            render={() =>
              renderProtectedPage(<ToolDatabase user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} />, {
                requiredPermission: "canUseCalculators",
              })
            }
          />

          <Route exact path="/fixtures" render={() =>
            renderProtectedPage(
              <FixtureLibrary user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} />,
              { requiredPermission: "canUseJobs" }
            )
          } />

          <Route exact path="/machines" render={() =>
            renderProtectedPage(
              <MachineDatabase user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} />,
              { requiredPermission: "canUseJobs" }
            )
          } />

          <Route exact path="/setup-sheets" render={() =>
            renderProtectedPage(
              <SetupSheets user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} />,
              { requiredPermission: "canUseJobs" }
            )
          } />

          <Route exact path="/job-travelers" render={() =>
            renderProtectedPage(
              <JobTravelers user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} />,
              { requiredPermission: "canUseJobs" }
            )
          } />

          <Route exact path="/inspection" render={() =>
            renderProtectedPage(
              <InspectionSheets user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} />,
              { requiredPermission: "canUseJobs" }
            )
          } />

          <Route exact path="/shop-brain" render={() =>
            renderProtectedPage(
              <ShopBrain user={user} accountProfile={accountProfile} readOnly={!activePermissions.canManageShopData} canExport={activePermissions.canExport} />,
              { requiredPermission: "canUseJobs" }
            )
          } />

          <Route exact path="/capacity-planner" render={() =>
            renderProtectedPage(<CapacityPlanner />, { requiredPermission: "canUseJobs" })
          } />

          <Route exact path="/shop-floor" render={() =>
            renderProtectedPage(<ShopFloorMode />, { requiredPermission: "canUseJobs" })
          } />

          <Route exact path="/ai-assistant" render={() =>
            renderProtectedPage(<AIAssistant accountProfile={accountProfile} />, { requiredPermission: "canUseCalculators" })
          } />

          <Route exact path="/qr-codes" render={() =>
            renderProtectedPage(<QRCodeCenter canExport={activePermissions.canExport} />, { requiredPermission: "canUseJobs" })
          } />

          <Route
            exact
            path="/exports"
            render={() =>
              renderProtectedPage(<ExportCenter user={user} />, {
                requiredPermission: "canExport",
              })
            }
          />

          <Route
            exact
            path="/3s"
            render={() =>
              renderProtectedPage(<ThreeSPage user={user} />, {
                requiredPermission: "canUseCalculators",
              })
            }
          />

          <Route
            exact
            path={APP_ROUTE_PATHS.calculator}
            render={({ match }) =>
              renderProtectedPage(
                <Calculator
                  user={user}
                  preferences={preferences}
                  toolId={match.params.toolId}
                  rolePermissions={activePermissions}
                />,
                { requiredPermission: "canUseCalculators" }
              )
            }
          />

          <Route render={() => <Redirect to="/" />} />
        </Switch>
      </div>
    </Router>
  );
}

export default App;
